import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { billingService } from 'services/billingService';
import { InvoiceStatus, IssueInvoiceDto } from 'types/models';

const INVOICES_KEY = 'billing-invoices';
const AR_KEY       = 'billing-ar-debtors';
const RECEIPTS_KEY = 'billing-receipts';

export const useInvoices = (status?: InvoiceStatus) =>
  useQuery({
    queryKey: [INVOICES_KEY, status ?? 'all'],
    queryFn:  () => billingService.listInvoices(status),
  });

export const usePatientInvoices = (patientId: string) =>
  useQuery({
    queryKey: [INVOICES_KEY, 'patient', patientId],
    queryFn:  () => billingService.listInvoicesByPatient(patientId),
    enabled:  !!patientId,
  });

export const useIssueInvoice = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ patientId, dto }: { patientId: string; dto: IssueInvoiceDto }) =>
      billingService.issueInvoice(patientId, dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [INVOICES_KEY] });
      qc.invalidateQueries({ queryKey: [AR_KEY] });
      qc.invalidateQueries({ queryKey: [RECEIPTS_KEY] });
    },
  });
};

export const useAuthorizeInvoice = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (invoiceId: string) => billingService.authorizeInvoice(invoiceId),
    onSuccess: () => qc.invalidateQueries({ queryKey: [INVOICES_KEY] }),
  });
};

export const useVoidInvoice = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (invoiceId: string) => billingService.voidInvoice(invoiceId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [INVOICES_KEY] });
      qc.invalidateQueries({ queryKey: [AR_KEY] });
      qc.invalidateQueries({ queryKey: [RECEIPTS_KEY] });
    },
  });
};

const triggerBlobDownload = (blob: Blob, filename: string) => {
  const url  = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href     = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

export const useDownloadInvoicePdf = () =>
  useMutation({
    mutationFn: ({ invoiceId, invoiceNumber }: { invoiceId: string; invoiceNumber: string | null }) =>
      billingService.downloadInvoicePdf(invoiceId),
    onSuccess: (blob, vars) => {
      triggerBlobDownload(blob, `factura-${vars.invoiceNumber ?? vars.invoiceId.slice(0, 8)}.pdf`);
    },
  });

export const useDownloadInvoiceXml = () =>
  useMutation({
    mutationFn: ({ invoiceId, invoiceNumber }: { invoiceId: string; invoiceNumber: string | null }) =>
      billingService.downloadInvoiceXml(invoiceId),
    onSuccess: (blob, vars) => {
      triggerBlobDownload(blob, `factura-${vars.invoiceNumber ?? vars.invoiceId.slice(0, 8)}.xml`);
    },
  });
